import React, {
	Fragment,
	useCallback,
	useEffect,
	useState,
	Suspense,
	createContext,
	useMemo,
	lazy,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, map, replace, startsWith, debounce } from 'lodash-es';
import { Grid, Box } from '@mui/material';
import Portal from '../../Components/Portal/Portal';
import FloAccessNag from '../../Components/FloAccessNag/FloAccessNag';
import { runFloSagas } from './Flo.sagas';

import {
	clearAnnotationsAction,
	getFloDetails,
	getFloSpaceDataAction,
	leaveFloPageAction,
	setAppComment,
	setFloErroredAction,
	setSettings,
} from './Flo.reducers';
import styles from './Flo.module.css';
import PrimaryHeader from '../../Components/PagePrimaryHeader/PagePrimaryHeader';
import qs from 'qs';
import classNames from 'classnames';
import { RootState } from '../../store';
import SendCommentPopup from './pages/GuideFlo/SendCommentPopup/SendCommentPopup';
import InternalArticleFooter from '../../Components/InternalArticleFooter/InternalArticleFooter';
import {
	enableTryFloikSelector,
	headerBrandImageSelector,
	enablePoweredBYSelector,
	hideTryFloikSelector,
} from '../../Common/Common.selector';
import { APP_ENDPOINT } from '../../Common/Common.env';
import {
	checkIsFloTokenValid,
	setFloAccessToken,
} from '../../Containers/Routes/Routes.reducers';
import { externalAppInfoType } from '../../Common/Common.types';
import { analyticsLog } from '../../store/reducers/Log.reducer';
import { hideLoader, showLoader } from '../../store/reducers/Loader.reducer';
import Notification from '../../Components/Notification/Notification';
import { NotificationType } from '../../Components/Notification/Notification.types';
import { setNotification } from '../../Components/Notification/Toasts.reducers';
import DialogComponent from '../../Components/Dialog/Dialog';
import {
	getContrastColor,
	getThemeDetails,
	ignoreSecurityCheck,
	floikColour,
	hexToRgb,
} from '../../Common/Common.utils';
import Background from '../../Components/BackgroundWrapper';
import { usePageVisibility } from '../../Common/Common.hooks';
import { initConfigMessageListener } from '../../utils/configMessageHandler';

const LazyFloOutputType = lazy(() => import('./pages/FloOutputType'));
const LazyLoginDialog = lazy(() => import('../../Components/LoginDialog/LoginDialog'));

const FloSkeleton = ({ themeColor = 'none' }: { themeColor?: string | null }) => {
	const safeColor = themeColor || 'none';
	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				minHeight: '200px',
				backgroundColor:
					safeColor && safeColor !== 'none' ? safeColor : 'rgba(255, 255, 255, 0.05)',
				opacity: 0.6,
			}}
		/>
	);
};

const appVersion = parseFloat(
	get(document.querySelector('meta[name="app-version"]'), 'content') || '1'
);

const queryParam = qs.parse(location.search.replace('?', ''));
const isEmbed = startsWith(location.pathname, '/embed/');
const isInApp = get(queryParam, 'inApp', 'false') === 'true';

let defaultValue = !isEmbed && appVersion > 1 ? 'false' : 'true';

export const ExternalAppInfoContext = createContext({});

// const hideHeader = get(queryParam, 'hide-header', defaultValue) === 'true';

const Flo = (props: {
	externalAppInfo?: externalAppInfoType;
	floId?: string | null;
	floIndex: number;
}) => {
	const { floId, floIndex } = props;
	const dispatch = useDispatch();
	const isPageHidden = usePageVisibility();

	const theme = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.theme`, 'none')
	);

	const animation =
		useSelector((state) =>
			get(state, `floPage.flos.${floIndex}.floDetails.animatedTheme`, null)
		) || null;

	const animatedThemeColor = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.animatedThemeColors`, 'none')
	);

	const outputType: string = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.outputType`, '')
	);
	const hideHeader = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["hide-header"]`, true)
	);
	const isInApp =
		document.querySelector(`[flo-id="${floId}"]`)?.getAttribute('floik-app') === 'true' ||
		get(queryParam, 'inApp', 'false') === 'true';

	const internalArticles = useSelector((state: RootState) =>
		hideHeader
			? false
			: get(state, `floPage.flos.${floIndex}.floDetails.internalArticles`, false)
	) as boolean;
	const showPoweredBy = useSelector(enablePoweredBYSelector);

	const publicResource = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.publicResource`)
	);

	const floAuthInfo = useSelector((state: RootState) =>
		get(state, `routerContainer.floAuthInfo.${floIndex}`)
	);

	let computedGlobalThemeColor = getComputedStyle(
		document.documentElement
	).getPropertyValue('--global-themeColor');
	let computedFloikDefaultThemeColor = getComputedStyle(
		document.documentElement
	).getPropertyValue('--global-floik-default-themeColor');

	const globalThemeColor = computedFloikDefaultThemeColor || computedGlobalThemeColor;
	const themeColor = getThemeDetails(theme) || 'none';

	const hideLogo = useSelector((state) =>
		get(state, `floPage.floSpace.branding.headerConfigurationData.hideHeaderLogo`)
	);

	const brandImage = useSelector(headerBrandImageSelector(floIndex));
	const enableTryFloik: boolean = useSelector(enableTryFloikSelector(floIndex));

	const hideTryFloik: boolean = useSelector(hideTryFloikSelector);

	const hideHeaderBecauseOfData = (hideLogo || !brandImage) && !enableTryFloik;
	const isEmbed = startsWith(location.pathname, '/embed/');

	useEffect(() => {
		// Clean up expired SSO sessions; persistent session validation is handled in getFloDetailsSaga via validate-session
		if (!publicResource && floId) {
			try {
				const sessionStr = localStorage.getItem('flo_user_session');
				if (sessionStr) {
					const session = JSON.parse(sessionStr);
					if (session.expiresAt <= Date.now()) {
						localStorage.removeItem('flo_user_session');
						localStorage.removeItem('flo_session_token');
						localStorage.removeItem('flo_content');
					}
				}
			} catch (e) {
				// ignore parse errors
			}
		}
	}, [floId, publicResource]);

	useEffect(() => {
		if (
			!isPageHidden &&
			!publicResource &&
			floId &&
			get(floAuthInfo, 'floAuthToken', '')
		) {
			const interval = setInterval(() => {
				dispatch(
					checkIsFloTokenValid({
						floId,
						token: get(floAuthInfo, 'floAuthToken', ''),
						floIndex,
					})
				);
			}, 60000);
			return () => {
				clearInterval(interval);
			};
		}
	}, [floAuthInfo, publicResource, isPageHidden]);

	const onMessage = useCallback((event: MessageEvent) => {
		if (event.origin !== APP_ENDPOINT) return;
		const type = get(event, 'data.type');
		const payload = get(event, 'data.payload') || {};
		switch (type) {
			case 'app-comment': {
				dispatch(
					setAppComment({
						data: {
							show: !!get(payload, 'canvasRenderConfig'),
							time: get(payload, 'time'),
							canvasRenderConfig: get(payload, 'canvasRenderConfig'),
							id: get(payload, 'id'),
						},
						floId,
						floIndex,
					})
				);
				dispatch(clearAnnotationsAction({ floId, floIndex }));
				break;
			}
			default:
				break;
		}
	}, []);

	useEffect(() => {
		let parentDomain = '';
		try {
			const domain = window.location !== window.parent.location ? document.referrer : '';
			parentDomain = replace(domain, /\/$/, '');
		} catch (e) {
			console.error(e);
		}
		if (parentDomain !== APP_ENDPOINT) return;

		window.parent.postMessage(
			{
				type: 'iframe_ready',
			},
			'*'
		);
		window.addEventListener('message', onMessage, true);
		return () => {
			window.removeEventListener('message', onMessage, true);
		};
	}, []);

	// Initialize config message listener for preview updates from parent app
	useEffect(() => {
		const cleanup = initConfigMessageListener(floIndex);
		return cleanup;
	}, [floIndex]);

	if (
		!isInApp &&
		floId &&
		!publicResource &&
		floAuthInfo &&
		!get(floAuthInfo, 'tokenValid')
	) {
		return (
			<Suspense fallback={null}>
				<LazyLoginDialog floId={floId} floIndex={floIndex} />
			</Suspense>
		);
	}

	return (
		<Background
			theme={themeColor}
			animatedThemeColor={animatedThemeColor}
			animation={animation}
		>
			<Grid
				container
				classes={{
					root: classNames(styles.gridContainer, styles.guideInstructions, {
						[styles.gridContainerWithWebsiteHeader]: internalArticles,
					}),
				}}
				sx={{
					background: animation
						? 'transparent'
						: themeColor === 'none'
						? 'transparent'
						: themeColor,
				}}
				style={
					{
						'--container-background': animation
							? 'transparent'
							: themeColor === 'none'
							? 'transparent'
							: themeColor,
					} as React.CSSProperties
				}
			>
				<FloAccessNag floId={floId} floIndex={floIndex}>
					<Suspense fallback={<FloSkeleton themeColor={themeColor} />}>
						<LazyFloOutputType
							hideHeader={true}
							floId={floId}
							hideHeaderBecauseOfData={hideHeaderBecauseOfData}
							floIndex={floIndex}
						/>
					</Suspense>
				</FloAccessNag>
				{!hideHeader && internalArticles && (
					<Grid item desktop={12} laptop={12} tablet={12} className="footer-sec">
						<InternalArticleFooter />
					</Grid>
				)}
				<SendCommentPopup floId={floId} floIndex={floIndex} />
				{!hideTryFloik && showPoweredBy && (
					<Box
						sx={{
							position: 'fixed',
							bottom: 0,
							left: 0,
							zIndex: 1000,
						}}
					>
						<svg width="120" height="20">
							<defs>
								<mask id="textCutout">
									<rect width="120" height="20" fill="white" />
									<text
										x="10" // Left padding
										y="18" // Vertical position (adjust for alignment)
										fontSize="11" // Text size
										fontWeight="400" // Regular weight for "Powered by"
										fill="black"
									>
										Powered by <tspan fontWeight="600">Floik</tspan>
									</text>
								</mask>
							</defs>

							<rect width="120" height="20" fill="#000000" mask="url(#textCutout)" />
						</svg>
					</Box>
				)}
			</Grid>
		</Background>
	);
};

const FlosList = (props: {
	externalAppInfo?: externalAppInfoType;
	floSpaceId?: string;
}) => {
	const embeddedElements = document.querySelectorAll('floik-flo');

	const [floIdState, setFloIdState] = useState('');

	const dispatch = useDispatch();

	const floSpace = useSelector((state) => get(state, 'floPage.floSpace'));

	useEffect(() => {
		runFloSagas();
		return () => {
			dispatch(leaveFloPageAction(''));
		};
	}, []);

	const fetchFloDetails = useCallback((flospaceId?: string) => {
		if (embeddedElements.length > 0) {
			for (let index = 0; index < embeddedElements.length; index++) {
				embeddedElements[index].classList.add('floik-container');
				dispatch(
					getFloDetails({
						customToken: get(props, 'externalAppInfo.token'),
						isPreview: get(props, 'externalAppInfo.isPreview'),
						floId: embeddedElements[index].getAttribute('flo-id'),
						floIndex: index,
						floSpaceId: flospaceId,
					})
				);
			}
		}
	}, []);

	useEffect(() => {
		if (ignoreSecurityCheck() && !get(props, 'floSpaceId')) {
			fetchFloDetails();
		} else {
			dispatch(
				getFloSpaceDataAction({
					floSpaceId: get(props, 'floSpaceId'),
					customToken: get(props, 'externalAppInfo.token'),
				})
			);
		}
	}, [fetchFloDetails]);

	useEffect(() => {
		if (get(props, 'floSpaceId') && get(floSpace, 'isDomainValid')) {
			fetchFloDetails(get(floSpace, 'flospaceId'));
		}
	}, [get(floSpace, 'flospaceId'), fetchFloDetails]);

	useEffect(() => {
		if (embeddedElements.length === 0 && !floIdState) {
			// getFloDetailsSaga (triggered by the getFloDetails dispatch below) owns
			// showLoader/hideLoader for the happy path, but if we bail out early -
			// no floId/folderId in the FLOIK_JSON payload, or the JSON.parse itself
			// throws - that saga never runs, so `loaders.flo_0` would otherwise stay
			// stuck at its initial value and GuideFlo never sees a loading -> ready
			// transition to animate off of. Mirror the show/hide here too so this
			// bootstrap path behaves the same as the saga-driven one.
			dispatch(showLoader(`flo_0`));
			try {
				const script = document.getElementById('FLOIK_JSON');
				const floId = script?.dataset?.floId;
				const folderId = script?.dataset?.folderId;
				const flospaceId = script?.dataset?.flospaceId;
				const id = floId || folderId;

				if (id) {
					// New attribute-based bootstrap: load flospace config in parallel
					// and let the saga fetch f_cfg.json using the flo/folder id.
					setFloIdState(id);
					if (flospaceId) {
						dispatch(
							getFloSpaceDataAction({
								floSpaceId: flospaceId,
								customToken: get(props, 'externalAppInfo.token'),
								floId: id,
							})
						);
					}
					dispatch(
						getFloDetails({
							customToken: get(props, 'externalAppInfo.token'),
							isPreview: get(props, 'externalAppInfo.isPreview'),
							floId: id,
							floIndex: 0,
							floSpaceId: flospaceId,
						})
					);
					dispatch(hideLoader(`flo_0`));
					return;
				}

				// Legacy inline-JSON bootstrap (old publishes where f_cfg.json is absent)
				let localData;
				if (process.env.NODE_ENV === 'development') {
					localData = localStorage.getItem('floik_json');
				}

				const json = JSON.parse(
					// @ts-ignore
					localData || script?.innerText
				);
				setFloIdState(get(json, 'floId', '') || get(json, 'folderId', ''));
				if (get(json, 'floId', '') || get(json, 'folderId', '')) {
					dispatch(
						getFloDetails({
							customToken: get(props, 'externalAppInfo.token'),
							isPreview: get(props, 'externalAppInfo.isPreview'),
							floId: get(json, 'floId', ''),
							floIndex: 0,
						})
					);
					// getFloDetailsSaga now owns the loading state with its own
					// showLoader/hideLoader pair, so release the one we took above.
					dispatch(hideLoader(`flo_0`));
				} else {
					dispatch(hideLoader(`flo_0`));
				}
			} catch (error) {
				dispatch(
					analyticsLog({
						level: 'error',
						message: `getFloDetailsSaga failed ${location.href} ${JSON.stringify(
							get(error, 'response')
						)}`,
						value: get(error, 'stack'),
					})
				);
				dispatch(
					setFloErroredAction({
						error: error,
						floId: '',
						floIndex: 0,
					})
				);
				// @ts-ignore
				if (![403, 404, 406].includes(get(error, 'response.status', 0))) {
					// dispatch(setErrorToast(e));
					console.log('error', error);
				}
				dispatch(setSettings({ floId: '', data: {}, floIndex: 0 }));
				dispatch(hideLoader(`flo_0`));
			}
		}
	}, [setFloIdState, floIdState]);

	const notifications: NotificationType[] = useSelector(
		// @ts-ignore
		(state) => get(state, `toasts`) as NotificationType[]
	);

	const onCloseNotification = useCallback((floIndex: number) => {
		dispatch(
			setNotification({
				data: {
					type: '',
					message: '',
					canShow: false,
				},
				floIndex,
			})
		);
	}, []);

	if (embeddedElements.length === 0) {
		return (
			<>
				<Flo
					floId={floIdState}
					floIndex={0}
					externalAppInfo={get(props, 'externalAppInfo')}
				/>
				<Notification
					type={get(notifications, `[0].type`, '')}
					message={get(notifications, `[0].message`, '')}
					canShow={get(notifications, `[0].canShow`, false)}
					onClose={() => onCloseNotification(0)}
					floIndex={0}
				/>
				<DialogComponent floIndex={0} />
			</>
		);
	}

	return (
		<div>
			{map(embeddedElements, (containerEl: HTMLElement, index: number) => (
				<Fragment key={containerEl.getAttribute('flo-id') || index}>
					<Portal target={containerEl}>
						<div className={styles.floContainer}>
							<ExternalAppInfoContext.Provider value={get(props, 'externalAppInfo', {})}>
								<Flo
									externalAppInfo={get(props, 'externalAppInfo')}
									floId={containerEl.getAttribute('flo-id')}
									floIndex={index}
								/>
							</ExternalAppInfoContext.Provider>
							<Notification
								type={get(notifications, `[${index}].type`, '')}
								message={get(notifications, `[${index}].message`, '')}
								canShow={get(notifications, `[${index}].canShow`, false)}
								onClose={() => onCloseNotification(index)}
								floIndex={index}
							/>
							<DialogComponent floIndex={index} />
						</div>
					</Portal>
				</Fragment>
			))}
		</div>
	);
};

export default FlosList;
